import H from '@here/maps-api-for-javascript'
import { useCallback, useEffect, useRef, useState } from 'react'

const ANIMATION_DURATION_MS = 500

/** Sinusoidal easing: slow-fast-slow */
const sinusoidalEase = (t: number) => (1 - Math.cos(Math.PI * t)) / 2

/**
 * useMarker is a low-level hook that puts a marker on a specific position on the map.
 * It portals React content directly into the HERE Maps clone element via onAttach/onDetach,
 * avoiding stale clones and expensive re-cloning on every update.
 *
 * @param map The HERE map instance.
 * @param position The position of the marker.
 * @param placement The placement of the marker. Defaults to 'center'.
 * @param zIndex The z-index of the marker.
 * @param visible Whether the marker should be shown on the map. Defaults to true.
 * @param animatePosition Whether position changes should animate smoothly. Defaults to false.
 * @returns `portalTarget` — the cloned DOM element to render into via `createPortal`,
 *   plus imperative handles `setPosition`, `setZIndex`, and `getPosition`.
 */
export const useMarker = ({
  map,
  position,
  placement = 'center',
  zIndex,
  visible = true,
  animatePosition = false,
}: {
  map?: H.Map
  position?: H.geo.IPoint
  placement?: 'center' | 'bottom-left'
  zIndex?: number
  visible?: boolean
  animatePosition?: boolean
}) => {
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null)
  const markerRef = useRef<H.map.DomMarker | null>(null)
  const addedToMapRef = useRef(false)
  const animationFrameRef = useRef<number | null>(null)
  const lastPositionRef = useRef<{ lat: number; lng: number } | null>(null)

  const cancelAnimation = useCallback(() => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }
  }, [])

  // Create marker once when map is available
  useEffect(() => {
    if (!map) return

    const template = document.createElement('div')

    const inner = document.createElement('div')
    inner.style.position = 'absolute'
    if (placement === 'center') {
      inner.style.transform = 'translate(-50%, -50%)'
    } else if (placement === 'bottom-left') {
      inner.style.transform = 'translateY(-100%)'
    }

    const icon = new H.map.DomIcon(template, {
      onAttach: (clonedElement: Element) => {
        clonedElement.appendChild(inner)
        setPortalTarget(inner)
      },
      onDetach: () => {
        setPortalTarget(null)
      },
    })

    const marker = new H.map.DomMarker({ lat: 0, lng: 0 }, { icon, data: {} })
    markerRef.current = marker
    addedToMapRef.current = false

    return () => {
      cancelAnimation()
      markerRef.current = null
      lastPositionRef.current = null
      setPortalTarget(null)
      if (addedToMapRef.current) {
        map.removeObject(marker)
        addedToMapRef.current = false
      }
    }
  }, [map, placement, cancelAnimation])

  // Update position and add/remove from map respecting visibility
  useEffect(() => {
    const marker = markerRef.current
    if (!marker || !map || !position) return

    cancelAnimation()

    const targetLat = position.lat
    const targetLng = position.lng

    if (animatePosition && lastPositionRef.current) {
      const startLat = lastPositionRef.current.lat
      const startLng = lastPositionRef.current.lng
      const startTime = performance.now()

      const animate = (now: number) => {
        const elapsed = now - startTime
        const progress = Math.min(elapsed / ANIMATION_DURATION_MS, 1)
        const eased = sinusoidalEase(progress)

        const lat = startLat + (targetLat - startLat) * eased
        const lng = startLng + (targetLng - startLng) * eased
        marker.setGeometry({ lat, lng })

        if (progress < 1) {
          animationFrameRef.current = requestAnimationFrame(animate)
        } else {
          animationFrameRef.current = null
          lastPositionRef.current = { lat: targetLat, lng: targetLng }
        }
      }

      animationFrameRef.current = requestAnimationFrame(animate)
    } else {
      marker.setGeometry(position)
      lastPositionRef.current = { lat: targetLat, lng: targetLng }
    }

    if (visible && !addedToMapRef.current) {
      map.addObject(marker)
      addedToMapRef.current = true
    }
  }, [map, position, visible, animatePosition, cancelAnimation])

  // Handle visibility changes independently of position
  useEffect(() => {
    const marker = markerRef.current
    if (!marker || !map) return

    if (visible && !addedToMapRef.current && lastPositionRef.current) {
      map.addObject(marker)
      addedToMapRef.current = true
    } else if (!visible && addedToMapRef.current) {
      map.removeObject(marker)
      addedToMapRef.current = false
    }
  }, [map, visible])

  // Update zIndex
  useEffect(() => {
    if (markerRef.current && zIndex !== undefined) {
      markerRef.current.setZIndex(zIndex)
    }
  }, [zIndex])

  // Imperative handles
  const setPosition = useCallback((lat: number, lng: number) => {
    if (markerRef.current) {
      markerRef.current.setGeometry({ lat, lng })
      lastPositionRef.current = { lat, lng }
    }
  }, [])

  const setZIndex = useCallback((value: number) => {
    if (markerRef.current) {
      markerRef.current.setZIndex(value)
    }
  }, [])

  const getPosition = useCallback(():
    | { lat: number; lng: number }
    | undefined => {
    return lastPositionRef.current ?? undefined
  }, [])

  return { portalTarget, setPosition, setZIndex, getPosition }
}
